/**
 * Custom DatePicker — ay/yıl başlığı sürekli görünür.
 *
 * Popup body'e portal edilir, ekran sınırlarına göre konumlanır.
 * Modal içinde de düzgün çalışır.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react'

interface Props {
  value: string
  onChange: (val: string) => void
  min?: string
  max?: string
  disabled?: boolean
  placeholder?: string
  className?: string
}

const AY_ADLARI = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
]
const GUN_KISA = ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pa']

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

function toIso(y: number, m: number, d: number): string {
  return `${y}-${pad(m + 1)}-${pad(d)}`
}

function parseIso(iso: string): { y: number; m: number; d: number } | null {
  if (!iso) return null
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null
  return { y: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]) }
}

function gunlerinSayisi(y: number, m: number): number {
  return new Date(y, m + 1, 0).getDate()
}

function ilkGunIndeksi(y: number, m: number): number {
  const d = new Date(y, m, 1).getDay()
  return d === 0 ? 6 : d - 1
}

function bugunIso(): string {
  const d = new Date()
  return toIso(d.getFullYear(), d.getMonth(), d.getDate())
}

const POPUP_W = 280
const POPUP_H = 340  // tahmini, akıllı flip için

export function DatePicker({
  value, onChange, min, max, disabled, placeholder, className = '',
}: Props) {
  const [acik, setAcik] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  const secili = parseIso(value)
  const bugun = parseIso(bugunIso())!
  const ilk = secili ?? bugun

  const [gosterilenY, setGosterilenY] = useState(ilk.y)
  const [gosterilenM, setGosterilenM] = useState(ilk.m)

  useEffect(() => {
    if (secili) {
      setGosterilenY(secili.y)
      setGosterilenM(secili.m)
    }
  }, [value]) // eslint-disable-line

  // Popup konumunu hesapla (trigger açısından)
  useLayoutEffect(() => {
    if (!acik || !triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight

    // Yatay: trigger'ın sol kenarı + popup sığar mı?
    let left = rect.left
    if (left + POPUP_W > vw - 8) {
      left = Math.max(8, rect.right - POPUP_W)  // sağa hizalı
    }
    if (left < 8) left = 8

    // Dikey: trigger'ın altına sığar mı, yoksa üstüne aç
    let top = rect.bottom + 8
    if (top + POPUP_H > vh - 8) {
      const ustTop = rect.top - POPUP_H - 8
      if (ustTop > 8) top = ustTop
    }
    setPos({ top, left })
  }, [acik])

  // Dışına tıkla → kapat (popup ve trigger hariç)
  useEffect(() => {
    if (!acik) return
    function tikla(e: MouseEvent) {
      const t = e.target as Node
      if (popupRef.current?.contains(t)) return
      if (triggerRef.current?.contains(t)) return
      setAcik(false)
    }
    function esc(e: KeyboardEvent) {
      if (e.key === 'Escape') setAcik(false)
    }
    document.addEventListener('mousedown', tikla)
    document.addEventListener('keydown', esc)
    // Scroll & resize ile pencereyi izle
    function yeniden() {
      if (!triggerRef.current) return
      const rect = triggerRef.current.getBoundingClientRect()
      const vw = window.innerWidth
      const vh = window.innerHeight
      let left = rect.left
      if (left + POPUP_W > vw - 8) left = Math.max(8, rect.right - POPUP_W)
      if (left < 8) left = 8
      let top = rect.bottom + 8
      if (top + POPUP_H > vh - 8) {
        const ustTop = rect.top - POPUP_H - 8
        if (ustTop > 8) top = ustTop
      }
      setPos({ top, left })
    }
    window.addEventListener('resize', yeniden)
    window.addEventListener('scroll', yeniden, true)
    return () => {
      document.removeEventListener('mousedown', tikla)
      document.removeEventListener('keydown', esc)
      window.removeEventListener('resize', yeniden)
      window.removeEventListener('scroll', yeniden, true)
    }
  }, [acik])

  function oncekiAy() {
    if (gosterilenM === 0) {
      setGosterilenY(gosterilenY - 1)
      setGosterilenM(11)
    } else setGosterilenM(gosterilenM - 1)
  }

  function sonrakiAy() {
    if (gosterilenM === 11) {
      setGosterilenY(gosterilenY + 1)
      setGosterilenM(0)
    } else setGosterilenM(gosterilenM + 1)
  }

  function gunSec(d: number) {
    const iso = toIso(gosterilenY, gosterilenM, d)
    if (min && iso < min) return
    if (max && iso > max) return
    onChange(iso)
    setAcik(false)
  }

  function bugunSec() {
    const iso = bugunIso()
    if (min && iso < min) return
    if (max && iso > max) return
    onChange(iso)
    const t = parseIso(iso)!
    setGosterilenY(t.y)
    setGosterilenM(t.m)
    setAcik(false)
  }

  function temizle() {
    onChange('')
  }

  const goster = secili
    ? `${secili.d} ${AY_ADLARI[secili.m]} ${secili.y}`
    : (placeholder ?? 'Tarih seç')

  const gunSayisi = gunlerinSayisi(gosterilenY, gosterilenM)
  const ilkIdx = ilkGunIndeksi(gosterilenY, gosterilenM)
  const hucreler: (number | null)[] = []
  for (let i = 0; i < ilkIdx; i++) hucreler.push(null)
  for (let d = 1; d <= gunSayisi; d++) hucreler.push(d)

  const popup = acik && pos ? createPortal(
    <>
      <div
        className="fixed inset-0 z-[55]"
        onClick={() => setAcik(false)}
        style={{ background: 'transparent' }}
      />
      <div
        ref={popupRef}
        className="fixed z-[60] bg-white border border-gray-200 rounded-2xl shadow-2xl p-3"
        style={{ top: pos.top, left: pos.left, width: POPUP_W }}
      >
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={oncekiAy}
            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-700"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-1">
            <select
              value={gosterilenM}
              onChange={(e) => setGosterilenM(Number(e.target.value))}
              className="font-bold text-gray-900 bg-transparent text-sm focus:outline-none cursor-pointer"
            >
              {AY_ADLARI.map((ad, i) => (
                <option key={i} value={i}>{ad}</option>
              ))}
            </select>
            <select
              value={gosterilenY}
              onChange={(e) => setGosterilenY(Number(e.target.value))}
              className="font-bold text-gray-900 bg-transparent text-sm focus:outline-none cursor-pointer"
            >
              {Array.from({ length: 121 }, (_, i) => 1930 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={sonrakiAy}
            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-700"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-1">
          {GUN_KISA.map((g) => (
            <div key={g} className="text-center text-[10px] font-bold text-gray-500 py-1">
              {g}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {hucreler.map((d, i) => {
            if (d === null) return <div key={`b${i}`} />
            const iso = toIso(gosterilenY, gosterilenM, d)
            const seciliMi = secili && iso === toIso(secili.y, secili.m, secili.d)
            const bugunMu = iso === bugunIso()
            const disabledHucre = (min && iso < min) || (max && iso > max)
            return (
              <button
                key={`d${d}`}
                type="button"
                onClick={() => gunSec(d)}
                disabled={!!disabledHucre}
                className={`aspect-square rounded-lg text-sm font-medium transition-colors ${
                  seciliMi
                    ? 'bg-brand-600 text-white shadow'
                    : bugunMu
                    ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-300'
                    : disabledHucre
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {d}
              </button>
            )
          })}
        </div>

        <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={temizle}
            className="text-xs text-gray-500 hover:text-red-600 inline-flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Temizle
          </button>
          <button
            type="button"
            onClick={bugunSec}
            className="text-xs text-brand-600 hover:text-brand-800 font-semibold"
          >
            Bugün
          </button>
        </div>
      </div>
    </>,
    document.body
  ) : null

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setAcik((a) => !a)}
        disabled={disabled}
        className={`input-base text-left flex items-center justify-between gap-2 ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400'
        } ${className}`}
      >
        <span className={secili ? 'text-gray-900' : 'text-gray-400'}>
          {goster}
        </span>
        <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0" />
      </button>
      {popup}
    </>
  )
}
